
import React, { useState } from "react";
import { LazyImage } from "./ui/lazy-image";
import { testimonials } from "@/data/testimonials";

const Testimonials = () => {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [showAllTestimonials, setShowAllTestimonials] = useState(false);
  
  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCards(newExpanded);
  };

  const truncateText = (text: string, maxLength: number = 300) => {
    if (text.length <= maxLength) return text;
    return text.slice(0, maxLength) + "...";
  };

  return (
    <section className="pt-16 2xl:pt-24 pb-24 2xl:pb-32 relative overflow-hidden bg-gradient-to-br from-background via-muted/30 to-background">
      {/* Modern background shapes */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-secondary/8 rounded-full blur-3xl animate-float-reverse"></div>
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-accent/6 rounded-full blur-3xl animate-float"></div>
        
        {/* Geometric shapes */}
        <div className="absolute top-1/4 right-1/3 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rotate-45 rounded-2xl blur-sm"></div>
        <div className="absolute bottom-1/3 left-1/4 w-24 h-24 bg-gradient-to-tl from-secondary/15 to-transparent rotate-12 rounded-xl blur-sm"></div>
      </div>
      
      <div className="container mx-auto px-4 2xl:px-12 max-w-none 2xl:max-w-[1600px] relative z-10">
        {/* Modern Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-6 py-3 bg-primary/10 rounded-full mb-8 backdrop-blur-sm border border-primary/20">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">
              Customer Stories
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl 2xl:text-6xl font-bold mb-8 leading-tight">
            Trusted by industry <span className="bg-gradient-to-r from-primary via-primary/80 to-accent bg-clip-text text-transparent font-bold">leaders</span>
          </h2>
          <p className="text-xl 2xl:text-2xl text-muted-foreground max-w-3xl 2xl:max-w-5xl mx-auto leading-relaxed">
            See how companies are transforming their culture with Assembly
          </p>
        </div>

        {/* Modern grid layout - show first 4 testimonials */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-7xl mx-auto">
          {testimonials.slice(0, 4).map((testimonial, index) => {
            const isExpanded = expandedCards.has(index);
            const shouldTruncate = testimonial.quote.length > 300;
            const displayText = isExpanded || !shouldTruncate 
              ? testimonial.quote 
              : truncateText(testimonial.quote);

            return (
              <div key={index} className="group relative">
                {/* Fixed height card for consistent alignment */}
                <div className="bg-card/80 backdrop-blur-sm p-6 rounded-2xl border border-border/50 relative overflow-hidden transition-all duration-500 hover:scale-[1.01] hover:shadow-xl hover:shadow-primary/10 h-[320px] flex flex-col">
                  {/* Dynamic background gradient */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  
                  {/* Content */}
                  <div className="relative z-10 flex flex-col h-full">
                    {/* Quote section with scrollable overflow when expanded */}
                    <div className={`flex-grow mb-4 ${isExpanded ? 'overflow-y-auto pr-2' : 'overflow-hidden'}`}>
                      <div className="text-4xl text-primary/20 font-serif absolute -top-2 -left-1 select-none">"</div>
                      <blockquote className="text-foreground/90 text-base leading-relaxed font-medium pl-6">
                        {displayText}
                        {shouldTruncate && (
                          <button
                            onClick={() => toggleExpanded(index)}
                            className="ml-2 text-primary hover:text-primary/80 font-semibold transition-colors text-sm block mt-2"
                          >
                            {isExpanded ? "Show less" : "Show more"}
                          </button>
                        )}
                      </blockquote>
                    </div>
                    
                    {/* Author section - always at same bottom position */}
                    <div className="flex items-center justify-between mt-auto flex-shrink-0 pt-4 border-t border-border/30">
                      <div className="flex items-center">
                        <LazyImage
                          src={testimonial.avatar} 
                          alt={testimonial.author}
                          className="w-full h-full object-cover"
                          containerClassName="w-12 h-12 rounded-xl overflow-hidden mr-3 ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all duration-300"
                          showSkeleton={true}
                        />
                        <div>
                          <h3 className="text-base font-bold text-foreground mb-0.5">{testimonial.author}</h3>
                          <p className="text-muted-foreground text-sm font-medium">{testimonial.title}</p>
                        </div>
                      </div>
                      
                      {/* Company logo */}
                      <div className="w-24 h-10 flex items-center justify-center opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                        <a 
                          href={testimonial.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full h-full flex items-center justify-center hover:scale-105 transition-transform duration-200"
                        >
                          <LazyImage
                            src={testimonial.logo} 
                            alt={`${testimonial.author}'s company logo`}
                            className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                            containerClassName="w-24 h-10 flex items-center justify-center"
                            showSkeleton={true}
                          />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Show more button */}
        {!showAllTestimonials && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAllTestimonials(true)}
              className="inline-flex items-center px-6 py-3 bg-primary/10 hover:bg-primary/20 rounded-full transition-all duration-300 border border-primary/20 hover:border-primary/30"
            >
              <span className="text-primary font-semibold">Show More Stories</span>
              <svg className="ml-2 w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        )}

        {/* 5th testimonial - shown when expanded */}
        {showAllTestimonials && (
          <div className="mt-8 flex justify-center">
            <div className="w-full max-w-md">
              {(() => {
                const testimonial = testimonials[4];
                const isExpanded = expandedCards.has(4);
                const shouldTruncate = testimonial.quote.length > 300;
                const displayText = isExpanded || !shouldTruncate 
                  ? testimonial.quote 
                  : truncateText(testimonial.quote);

                return (
                  <div className="group relative">
                    {/* Fixed height card for consistent alignment */}
                    <div className="bg-card/80 backdrop-blur-sm p-6 rounded-2xl border border-border/50 relative overflow-hidden transition-all duration-500 hover:scale-[1.01] hover:shadow-xl hover:shadow-primary/10 h-[320px] flex flex-col">
                      {/* Dynamic background gradient */}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                      
                      {/* Content */}
                      <div className="relative z-10 flex flex-col h-full">
                        {/* Quote section with scrollable overflow when expanded */}
                        <div className={`flex-grow mb-4 ${isExpanded ? 'overflow-y-auto pr-2' : 'overflow-hidden'}`}>
                          <div className="text-4xl text-primary/20 font-serif absolute -top-2 -left-1 select-none">"</div>
                          <blockquote className="text-foreground/90 text-base leading-relaxed font-medium pl-6">
                            {displayText}
                            {shouldTruncate && (
                              <button
                                onClick={() => toggleExpanded(4)}
                                className="ml-2 text-primary hover:text-primary/80 font-semibold transition-colors text-sm block mt-2"
                              >
                                {isExpanded ? "Show less" : "Show more"}
                              </button>
                            )}
                          </blockquote>
                        </div>
                        
                        {/* Author section - always at same bottom position */}
                        <div className="flex items-center justify-between mt-auto flex-shrink-0 pt-4 border-t border-border/30">
                          <div className="flex items-center">
                            <LazyImage
                              src={testimonial.avatar} 
                              alt={testimonial.author}
                              className="w-full h-full object-cover"
                              containerClassName="w-12 h-12 rounded-xl overflow-hidden mr-3 ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all duration-300"
                              showSkeleton={true}
                            />
                            <div>
                              <h3 className="text-base font-bold text-foreground mb-0.5">{testimonial.author}</h3>
                              <p className="text-muted-foreground text-sm font-medium">{testimonial.title}</p>
                            </div>
                          </div>
                          
                          {/* Company logo */}
                          <div className="w-24 h-10 flex items-center justify-center opacity-60 group-hover:opacity-100 transition-opacity duration-300">
                            <a 
                              href={testimonial.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full h-full flex items-center justify-center hover:scale-105 transition-transform duration-200"
                            >
                              <LazyImage
                                src={testimonial.logo} 
                                alt={`${testimonial.author}'s company logo`}
                                className="max-w-full max-h-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-300"
                                containerClassName="w-24 h-10 flex items-center justify-center"
                                showSkeleton={true}
                              />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
